'use client'

import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence, LayoutGroup } from 'motion/react'
import {
  SunIcon,
  CalendarBlankIcon,
  FolderSimpleIcon,
  NotebookIcon,
  FilmSlateIcon,
  BookOpenIcon,
  MusicNoteIcon,
  UsersIcon,
  CaretRightIcon,
} from '@phosphor-icons/react'
import type { Icon as PhosphorIcon } from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'
import { useProjects } from '@/projects'
import { useTaskTopicCounts } from '@/entries'
import { useShortcutDiscovery } from './shortcut-discovery'
import { navItemTestId, type NavItemId } from './test-ids'

// ---------------------------------------------------------------------------
// Nav config types
// ---------------------------------------------------------------------------

interface NavItemConfig {
  // Typed against the test-id leaf: a nav item with no id there cannot be named by a spec.
  id: NavItemId
  label: string
  href: string
  icon: PhosphorIcon
  expandable?: boolean
}

interface NavSectionConfig {
  id: string
  label: string
  items: NavItemConfig[]
}

// ---------------------------------------------------------------------------
// Declarative config
// ---------------------------------------------------------------------------

const NAV_CONFIG: NavSectionConfig[] = [
  {
    id: 'focus',
    label: 'Focus',
    items: [
      { id: 'today', label: 'Today', href: '/app', icon: SunIcon },
      {
        id: 'upcoming',
        label: 'Upcoming',
        href: '/app/upcoming',
        icon: CalendarBlankIcon,
      },
    ],
  },
  {
    id: 'organize',
    label: 'Organize',
    items: [
      {
        id: 'projects',
        label: 'Projects',
        href: '/app/projects',
        icon: FolderSimpleIcon,
        expandable: true,
      },
      {
        id: 'journal',
        label: 'Journal',
        href: '/app/journal',
        icon: NotebookIcon,
      },
      { id: 'people', label: 'People', href: '/app/people', icon: UsersIcon },
    ],
  },
  {
    id: 'library',
    label: 'Library',
    items: [
      {
        id: 'movies',
        label: 'Movies',
        href: '/app/movies',
        icon: FilmSlateIcon,
      },
      {
        id: 'books',
        label: 'Books',
        href: '/app/books',
        icon: BookOpenIcon,
      },
      {
        id: 'music',
        label: 'Music',
        href: '/app/music',
        icon: MusicNoteIcon,
      },
    ],
  },
] satisfies NavSectionConfig[]

// ---------------------------------------------------------------------------
// Count helpers (hooks called at the top of SidebarNav)
// ---------------------------------------------------------------------------

function useSidebarCounts() {
  const { totalTaskCount } = useTaskTopicCounts()
  return { today: totalTaskCount }
}

// ---------------------------------------------------------------------------
// Project list (expandable)
// ---------------------------------------------------------------------------

function ProjectList({ collapsed }: { collapsed: boolean }) {
  const { data: projects = [] } = useProjects()
  const { topics } = useTaskTopicCounts()
  const pathname = usePathname()

  if (collapsed || projects.length === 0) return null

  // Build a map of project-id -> incomplete task count from topics
  const countByProject = new Map<string, number>()
  for (const t of topics) {
    countByProject.set(t.id, t.count)
  }

  return (
    <ul
      className="ml-6 max-h-48 space-y-0.5 overflow-y-auto"
      role="list"
      aria-label="Project list"
    >
      {projects.map((project) => {
        const href = `/app/projects/${project.id}`
        const isActive = pathname === href
        const count = countByProject.get(project.id) ?? 0

        return (
          <li key={project.id}>
            <Link
              href={href}
              className={cn(
                'flex items-center justify-between rounded-md px-2 py-1 text-sm transition-colors',
                isActive
                  ? 'border-accent bg-accent/5 text-foreground border-l-2'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/5',
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="truncate">{project.name}</span>
              {count > 0 && (
                <span className="text-muted-foreground text-xs tabular-nums">
                  {count}
                </span>
              )}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// NavItem
// ---------------------------------------------------------------------------

interface NavItemProps {
  item: NavItemConfig
  collapsed: boolean
  isActive: boolean
  count?: number
  expanded?: boolean
  onToggleExpand?: () => void
  onClickItem?: (itemId: string) => void
}

function NavItem({
  item,
  collapsed,
  isActive,
  count,
  expanded,
  onToggleExpand,
  onClickItem,
}: NavItemProps) {
  const IconComponent = item.icon

  return (
    <Link
      href={item.href}
      onClick={() => onClickItem?.(item.id)}
      data-testid={navItemTestId(item.id)}
      className={cn(
        'group relative flex min-h-[36px] items-center gap-3 rounded-md px-2 py-1.5 text-sm transition-colors',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        isActive
          ? 'bg-accent/5 text-foreground'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/5',
        collapsed && 'justify-center px-0',
      )}
      aria-current={isActive ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      title={collapsed ? item.label : undefined}
    >
      {isActive && (
        <motion.div
          layoutId="sidebar-active-indicator"
          className="bg-accent absolute top-0 left-0 h-full w-0.5 rounded-full"
          transition={{ duration: 0.15, ease: 'easeOut' }}
        />
      )}
      <IconComponent className="h-[18px] w-[18px] shrink-0" weight="regular" />
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.expandable && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onToggleExpand?.()
              }}
              className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors"
              aria-label={
                expanded ? `Collapse ${item.label}` : `Expand ${item.label}`
              }
            >
              <CaretRightIcon
                className={cn(
                  'h-3.5 w-3.5 transition-transform duration-150',
                  expanded && 'rotate-90',
                )}
                weight="bold"
              />
            </button>
          )}
          {!item.expandable && count !== undefined && count > 0 && (
            <AnimatePresence mode="popLayout">
              <motion.span
                key={count}
                className="text-muted-foreground text-xs tabular-nums"
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                {count}
              </motion.span>
            </AnimatePresence>
          )}
        </>
      )}
    </Link>
  )
}

// ---------------------------------------------------------------------------
// NavSection
// ---------------------------------------------------------------------------

interface NavSectionProps {
  section: NavSectionConfig
  collapsed: boolean
  counts: Record<string, number>
  pathname: string
  onClickItem?: (itemId: string) => void
}

function NavSection({
  section,
  collapsed,
  counts,
  pathname,
  onClickItem,
}: NavSectionProps) {
  const [projectsExpanded, setProjectsExpanded] = useState(true)

  const isItemActive = (item: NavItemConfig) => {
    if (item.href === '/app') {
      return pathname === '/app' || pathname === '/'
    }
    return pathname.startsWith(item.href)
  }

  return (
    <div className="space-y-0.5" role="group" aria-label={section.label}>
      <ul className="space-y-0.5" role="list">
        {section.items.map((item) => {
          const isActive = isItemActive(item)
          return (
            <li key={item.id}>
              <NavItem
                item={item}
                collapsed={collapsed}
                isActive={isActive}
                count={counts[item.id]}
                expanded={item.expandable ? projectsExpanded : undefined}
                onToggleExpand={
                  item.expandable
                    ? () => setProjectsExpanded((prev) => !prev)
                    : undefined
                }
                onClickItem={onClickItem}
              />
              {item.expandable && projectsExpanded && (
                <ProjectList collapsed={collapsed} />
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SidebarNav (exported)
// ---------------------------------------------------------------------------

interface SidebarNavProps {
  collapsed: boolean
}

export function SidebarNav({ collapsed }: SidebarNavProps) {
  const pathname = usePathname()
  const { today } = useSidebarCounts()
  const { showHint } = useShortcutDiscovery()

  const counts: Record<string, number> = {
    today,
  }

  return (
    <LayoutGroup>
      <nav className="flex flex-col" role="navigation" aria-label="Sidebar">
        {NAV_CONFIG.map((section, index) => (
          <div key={section.id}>
            {index > 0 && (
              <div className={cn('my-1.5', collapsed ? 'mx-1.5' : 'mx-2')}>
                <div className="border-border/30 border-t" />
              </div>
            )}
            <NavSection
              section={section}
              collapsed={collapsed}
              counts={counts}
              pathname={pathname}
              onClickItem={showHint}
            />
          </div>
        ))}
      </nav>
    </LayoutGroup>
  )
}

export { NAV_CONFIG }
export type { NavSectionConfig, NavItemConfig }
